import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import {
  ArrowLeft,
  Download,
  Printer,
  Bluetooth,
  Loader2,
  Info,
  CheckCircle2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  downloadPng,
  browserPrint,
  sendToBluetooth,
  isBluetoothSupported,
} from "@/lib/printerService";

export default function PrintLabelPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // 'download' | 'print' | 'bt' | null

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/customers/${id}`);
        if (alive) setCustomer(data);
      } catch (e) {
        toast.error(formatApiError(e));
        navigate("/kunden");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [id, navigate]);

  const filename = customer
    ? `1ai-review-label-50mm-${customer.slug}-${customer.stand_code}.png`
    : "label.png";

  const handleDownload = async () => {
    setBusy("download");
    try {
      await downloadPng(customer.id, filename, { sizeMm: 50, dpi: 300 });
      toast.success("Label als PNG heruntergeladen.");
      if (customer && !customer.activation_checklist?.qr_downloaded) {
        try {
          await api.patch(`/customers/${customer.id}/activation`, {
            key: "qr_downloaded",
            value: true,
          });
        } catch {
          /* soft-fail */
        }
      }
    } catch {
      toast.error("Download fehlgeschlagen.");
    } finally {
      setBusy(null);
    }
  };

  const handlePrint = async () => {
    setBusy("print");
    try {
      await browserPrint(customer.id, { sizeMm: 50, dpi: 300 });
      toast.success("Druckdialog geöffnet. Wähle deinen NIIMBOT B1 als Drucker.");
    } catch {
      toast.error("Druck konnte nicht gestartet werden.");
    } finally {
      setBusy(null);
    }
  };

  const handleBluetooth = async () => {
    setBusy("bt");
    try {
      const res = await sendToBluetooth();
      toast.message(res.message || "Bluetooth-Druck folgt.");
    } finally {
      setBusy(null);
    }
  };

  if (loading || !customer) {
    return (
      <div className="text-muted-foreground text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Lädt…
      </div>
    );
  }

  const btSupported = isBluetoothSupported();

  return (
    <div className="space-y-6" data-testid="print-label-page">
      <div className="flex items-center justify-between gap-3">
        <Link
          to={`/kunden/${customer.id}`}
          className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1.5"
          data-testid="back-to-customer"
        >
          <ArrowLeft className="w-3 h-3" /> Zurück zum Kunden
        </Link>
        <span className="text-xs text-muted-foreground">
          NIIMBOT B1 · 50 × 50 mm
        </span>
      </div>

      <div>
        <p className="label-eyebrow">Label drucken</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight mt-1 break-words">
          {customer.business_name}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Druckfertiges 50 × 50 mm Label mit QR-Code für den NFC-Aufsteller.
        </p>
      </div>

      <div className="grid lg:grid-cols-[auto,1fr] gap-6 items-start">
        {/* 1:1 Preview */}
        <div className="card-surface p-5" data-testid="label-preview-card">
          <div className="label-eyebrow mb-3">1 : 1 Vorschau (50 × 50 mm)</div>
          <div
            className="bg-white rounded-md shadow-inner flex flex-col items-center justify-center p-2 mx-auto"
            style={{ width: "50mm", height: "50mm" }}
            data-testid="label-preview-canvas"
          >
            <div className="grow flex items-center justify-center">
              <QRCodeCanvas
                value={customer.nfc_link}
                size={140}
                level="H"
                marginSize={2}
                fgColor="#000000"
                bgColor="#ffffff"
              />
            </div>
            <div className="text-[10px] font-semibold text-black leading-tight">
              Scannen &amp; bewerten
            </div>
            <div className="text-[9px] font-mono text-black/80 leading-tight">
              ID: {customer.stand_code}
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3 text-center">
            Vorschau in physikalischer Größe. Auf Papier so groß wie hier.
          </p>
        </div>

        {/* Actions */}
        <div className="space-y-4">
          <div className="card-surface p-5">
            <h3 className="font-heading text-lg font-semibold mb-1">
              Druck-Optionen
            </h3>
            <p className="text-xs text-muted-foreground mb-4">
              Der QR-Code zeigt auf dieselbe Adresse wie der NFC-Chip
              (<span className="font-mono">{customer.nfc_link}</span>).
            </p>

            <div className="flex flex-col gap-2">
              <Button
                onClick={handleDownload}
                disabled={busy !== null}
                className="w-full rounded-full gap-2"
                data-testid="label-download-btn"
              >
                {busy === "download" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                PNG herunterladen (591 × 591 px, 300 DPI)
              </Button>

              <Button
                onClick={handlePrint}
                variant="secondary"
                disabled={busy !== null}
                className="w-full rounded-full gap-2"
                data-testid="label-print-btn"
              >
                {busy === "print" ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Printer className="w-4 h-4" />
                )}
                Sofort drucken (System-Dialog)
              </Button>

              <Button
                onClick={handleBluetooth}
                variant="ghost"
                disabled
                className="w-full rounded-full gap-2 opacity-70"
                title="In Kürze verfügbar"
                data-testid="label-bluetooth-btn"
              >
                <Bluetooth className="w-4 h-4" />
                An NIIMBOT B1 senden (Bluetooth)
                <span className="ml-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                  in Kürze
                </span>
              </Button>
            </div>

            <Separator className="my-4 bg-white/[0.06]" />

            <ul className="text-xs text-muted-foreground space-y-2">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-400 shrink-0" />
                Format: 50 × 50 mm — passt exakt auf NIIMBOT B1 Etiketten.
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 text-emerald-400 shrink-0" />
                Auflösung: 300 DPI, ECC-Level H (30 % Fehlerkorrektur).
              </li>
              <li className="flex items-start gap-2">
                <Info className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                Für System-Druck: NIIMBOT B1 zuvor im Betriebssystem koppeln
                (USB oder Wi-Fi), dann &bdquo;Sofort drucken&ldquo; w&auml;hlen.
              </li>
              {!btSupported && (
                <li className="flex items-start gap-2">
                  <Info className="w-3.5 h-3.5 mt-0.5 text-amber-400 shrink-0" />
                  Web-Bluetooth wird in deinem Browser nicht unterstützt.
                  Nutze aktuell den PNG- oder System-Druck.
                </li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
